/**
 * 地界的房子：一栋「建筑物物」，画的是这家门脸，匾额上写着地名。
 *
 * 这些东西不是家具，玩家布置不来（没有 cost 就不在什物单里），
 * 它们由场景自己带着：一处进得去的屋子，门口就该有一栋房子。
 */

import { registerProp } from './items.js';
import { sceneInfo } from './world.js';
import { BUILDING_PRESETS, PLACE_TYPES } from '../render/buildings_art.js';

/** 每样房子占几格（宽 x 高） */
export const PLACE_SIZES = {
  shop: [3, 2], general: [3, 2], eatery: [3, 2], library: [3, 2], clinic: [3, 2],
  pawn: [3, 2], bath: [3, 2], home: [3, 2], carport: [3, 2],
  garage: [4, 2], junk: [4, 2], depot: [4, 2], fun: [4, 2], pet: [4, 2],
  inn: [4, 3], temple: [4, 3], yamen: [5, 3], gate: [5, 3],
};

/** 猜不出牌子的时候，看名字里的字眼定样式 */
const KEYWORDS = [
  [/carport/, 'carport'],
  [/garage|车铺/, 'garage'],
  [/inn|lodg|车店/, 'inn'],
  [/pawn|当铺/, 'pawn'],
  [/yamen|magistrate|衙/, 'yamen'],
  [/bath|onsen|汤/, 'bath'],
  [/fun|瓦舍|戏/, 'fun'],
  [/depot|warehouse|货栈/, 'depot'],
  [/junk|scrap|破烂/, 'junk'],
  [/library|book|书/, 'library'],
  [/pharm|clinic|药/, 'clinic'],
  [/pet|livestock|牲口/, 'pet'],
  [/temple|shrine|庙|观/, 'temple'],
  [/gate|关|城门/, 'gate'],
  [/home|house|邻/, 'home'],
  [/eater|restaurant|食/, 'eatery'],
];

export function pickBuildingKind(id, info = {}) {
  const key = info.shopId || id;
  if (BUILDING_PRESETS[key]) return key;
  const hay = `${id} ${info.shopId || ''} ${info.name || ''}`;
  for (const [re, kind] of KEYWORDS) {
    if (re.test(hay)) return kind;
  }
  return 'shop';
}

export const PLACE_PROPS = {};
for (const kind of PLACE_TYPES) {
  const [w, h] = PLACE_SIZES[kind] || [3, 2];
  PLACE_PROPS[`place_${kind}`] = {
    name: '地界房子',
    w, h, layer: 'furniture', solid: true,
    desc: '一处有屋顶的所在，匾额上写着地名。',
    state: { name: '', kind },
  };
}

export function installPlaceProps() {
  for (const [id, def] of Object.entries(PLACE_PROPS)) registerProp(id, def);
  return true;
}

/* ------------------------------ 门口起屋 ------------------------------ */

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/** 两个格子范围是否相交 */
function overlap(a, b) {
  return a.gx < b.gx + b.w && b.gx < a.gx + a.w && a.gy < b.gy + b.h && b.gy < a.gy + a.h;
}

/**
 * 按场景文档算出一处户人家该起的房子。
 * 规矩很简单：进得去的屋子（indoor）才给盖；房子坐在门户的北边，
 * 门户落在南墙之外，人从门洞下去，不会被自家的墙挤住。
 *
 * @returns {Array<{type:string,gx:number,gy:number,state:object}>} 可以直接入库的行
 */
export function placeBuildingsForScene(doc) {
  const rows0 = doc.rows || [];
  if (!rows0.length) return [];
  const gw = Math.max(...rows0.map((r) => r.length));
  const gh = rows0.length;
  const portals = (doc.props || []).filter((p) => p.type === 'portal' && p.state && p.state.to);
  // 别人家的门口与人落脚的地方要留出进出的一条缝，自己那扇门也压不得
  const spawnKeep = doc.spawn ? { gx: doc.spawn.gx - 1, gy: doc.spawn.gy - 1, w: 3, h: 3 } : null;

  const taken = [];
  const seen = new Set();
  const out = [];
  for (const p of portals) {
    const to = p.state.to;
    if (seen.has(to)) continue;
    seen.add(to);
    const info = sceneInfo(to);
    if (!info.indoor) continue;                     // 敞地里不凭空起楼

    const kind = pickBuildingKind(to, info);
    const [bw, bh] = PLACE_SIZES[kind] || [3, 2];
    const gx = clamp(Math.round(p.gx - (bw - 1) / 2), 1, Math.max(1, gw - bw - 1));
    const gy = p.gy - bh;                           // 坐在门户北边
    // 门开在北墙根（门北没地方）就不盖：房子是实心的，挪到门南边会把自家门堵死
    if (gy < 1) continue;
    const box = { gx, gy, w: bw, h: bh };
    const friends = portals.filter((q) => q !== p)
      .map((q) => ({ gx: q.gx - 1, gy: q.gy - 1, w: 3, h: 3 }));
    if (friends.some((k) => overlap(box, k))) continue;                 // 别把别人家堵死
    if (overlap(box, { gx: p.gx, gy: p.gy, w: 1, h: 1 })) continue;     // 自家门口也得留出人站的地
    if (spawnKeep && overlap(box, spawnKeep)) continue;                 // 出生点上不能盖房
    if (taken.some((k) => overlap(box, k))) continue;                   // 跟别家叠在一起也算了
    taken.push(box);
    out.push({
      type: `place_${kind}`,
      gx, gy,
      state: { name: info.name || to, kind },
    });
  }
  return out;
}
